import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const SHARE_URL = "https://mybotspay.me";
const SHARE_TEXT =
  "Check out EazyBot — trading bots engineered to earn consistent profits. Free EazyBot class every Wednesday at 8pm ET.";

export const ShareModal = ({ open, onClose }) => {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const fallbackCopy = () => {
    if (!inputRef.current) return false;
    inputRef.current.focus();
    inputRef.current.select();
    inputRef.current.setSelectionRange(0, inputRef.current.value.length);
    return document.execCommand("copy");
  };

  const handleCopy = async () => {
    let copiedSuccessfully = false;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(SHARE_URL);
        copiedSuccessfully = true;
      } catch {
        copiedSuccessfully = fallbackCopy();
      }
    } else {
      copiedSuccessfully = fallbackCopy();
    }

    if (copiedSuccessfully) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  if (!open) return null;

  const smsHref = `sms:?&body=${encodeURIComponent(SHARE_TEXT + " " + SHARE_URL)}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(SHARE_TEXT + " " + SHARE_URL)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent("EazyBot — Trading Bots")}&body=${encodeURIComponent(SHARE_TEXT + "\n\n" + SHARE_URL)}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-modal-title"
      data-testid="share-modal"
    >
      <div
        className="absolute inset-0"
        style={{
          background: "rgba(2,7,18,0.78)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
        onClick={onClose}
        data-testid="share-modal-backdrop"
      />

      <div
        ref={dialogRef}
        className="relative w-full max-w-lg rounded-2xl p-6 md:p-8"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,30,55,0.96) 0%, rgba(8,17,33,0.96) 100%)",
          border: "1px solid rgba(3,172,239,0.45)",
          boxShadow:
            "0 0 0 1px rgba(3,172,239,0.15), 0 24px 60px rgba(0,0,0,0.6), 0 0 50px rgba(3,172,239,0.18)",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: "#03ACEF", boxShadow: "0 0 10px #03ACEF" }}
            />
            <span
              className="font-mono-label text-[11px]"
              style={{ color: "#03ACEF" }}
            >
              Share
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close share dialog"
            className="h-9 w-9 rounded-full border flex items-center justify-center transition-colors"
            style={{
              borderColor: "rgba(3,172,239,0.35)",
              color: "rgba(255,255,255,0.85)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(3,172,239,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
            data-testid="share-modal-close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <h3
          id="share-modal-title"
          className="mt-5 font-display text-white leading-tight"
          style={{ fontSize: "clamp(1.4rem, 2.4vw, 1.85rem)" }}
        >
          Invite friends &amp; family to a class
        </h3>
        <p className="mt-2 text-sm md:text-base text-white/65">
          Share MyBotsPay.me via your favorite app.
        </p>

        <div className="mt-6 grid grid-cols-3 gap-3 md:gap-4">
          <ShareTile
            href={smsHref}
            label="Text Message"
            testid="share-sms"
            icon={
              <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                <path
                  d="M21 12a8 8 0 1 1-3.07-6.31L21 4l-1 4.3A7.97 7.97 0 0 1 21 12Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          />
          <ShareTile
            href={waHref}
            external
            label="WhatsApp"
            testid="share-whatsapp"
            icon={
              <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                <path
                  d="M20.5 11.6A8.5 8.5 0 0 0 4.7 17.3L3.5 21l3.8-1.2a8.5 8.5 0 0 0 13.2-8.2Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 9.5c.4 1.4 1.6 3 3 4 1.4 1 2.8 1.3 3.5 1.3"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            }
          />
          <ShareTile
            href={mailHref}
            label="Email"
            testid="share-email"
            icon={
              <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                <rect
                  x="3"
                  y="5"
                  width="18"
                  height="14"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <path
                  d="m4 7 8 6 8-6"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
            }
          />
        </div>

        <div className="mt-6">
          <div
            className="flex items-center gap-3 rounded-lg px-3 md:px-4 py-3"
            style={{
              border: "1px solid rgba(3,172,239,0.35)",
              background: "rgba(3,172,239,0.05)",
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              className="h-4 w-4 shrink-0"
              style={{ color: "#03ACEF" }}
              aria-hidden="true"
            >
              <path
                d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 1 0-7-7l-1 1M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 1 0 7 7l1-1"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              readOnly
              value={SHARE_URL}
              className="flex-1 bg-transparent outline-none text-sm md:text-base text-white/90"
              onFocus={(e) => e.target.select()}
              data-testid="share-url-input"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="font-mono-label text-[11px] px-3 py-1.5 rounded-full transition-colors"
              style={{
                color: copied ? "#141034" : "#03ACEF",
                background: copied ? "#03ACEF" : "transparent",
                border: "1px solid rgba(3,172,239,0.55)",
              }}
              data-testid="share-copy-button"
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const ShareTile = ({ href, label, icon, testid, external }) => {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="flex flex-col items-center justify-center gap-2 rounded-xl p-4 md:p-5 transition-colors"
      style={{
        border: "1px solid rgba(3,172,239,0.32)",
        background: "rgba(3,172,239,0.04)",
        color: "#03ACEF",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(3,172,239,0.14)";
        e.currentTarget.style.borderColor = "rgba(3,172,239,0.6)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(3,172,239,0.04)";
        e.currentTarget.style.borderColor = "rgba(3,172,239,0.32)";
      }}
      data-testid={testid}
    >
      <div style={{ color: "#03ACEF" }}>{icon}</div>
      <span className="text-xs md:text-sm font-medium text-white/90">
        {label}
      </span>
    </a>
  );
};
